import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Search, UserX } from "lucide-react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { OaReasonDialog } from "./OaReasonDialog";

// Otter Assist admin — User Access (OAD-119) and per-user Overrides (OAD-120).
//
// Access disable is FEATURE-SCOPED and never retroactive. Overrides are append-only: revoking stamps
// a record, expiry is lazy, and `expires_at` empty means PERMANENT.

const KIND_LABEL = {
  allowance: "Monthly Assist allowance",
  max_open: "Maximum concurrent open Assists",
  daily_capacity: "Recipient daily delivery capacity",
  digest_daily_cap: "Digest daily email cap",
};

const DISABLE_EFFECT =
  "Blocks new Assists, new Community Needs, new I Can Help offers and new digest emails. "
  + "History, existing offers and an already-charged active Assist are untouched.";

export function OaAccess() {
  const [lookup, setLookup] = useState("");
  const [query, setQuery] = useState("");
  const [userId, setUserId] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [kind, setKind] = useState("allowance");
  const [value, setValue] = useState("");
  const [expires, setExpires] = useState("");

  // Reuses the EXISTING admin member search (`GET /api/admin/users?q=`) rather than a new
  // directory subsystem. An admin picks a real member; the mutation then always carries the
  // canonical `user_id`, and the backend re-resolves it regardless (Phase 9 F1).
  const search = useQuery({
    queryKey: ["oa-admin-member-search", query],
    queryFn: async () => (await api.get("/admin/users", {
      params: { q: query, page_size: 8, sort: "name_asc" },
    })).data,
    enabled: query.length >= 2,
  });

  const access = useQuery({
    queryKey: ["oa-admin-access", userId],
    queryFn: async () => (await api.get(`/admin/otter-assist/access/${userId}`)).data,
    enabled: !!userId,
  });
  const overrides = useQuery({
    queryKey: ["oa-admin-overrides", userId],
    queryFn: async () => (await api.get(`/admin/otter-assist/overrides/${userId}`)).data,
    enabled: !!userId,
  });

  const member = access.data?.member || overrides.data?.member || null;
  const memberLine = member
    ? `${member.name || "(no name)"} · ${member.email || "(no email)"} · ${member.user_id}`
    : userId;
  const disabled = access.data?.state?.state === "disabled";

  return (
    <div className="space-y-4" data-testid="oa-admin-access">
      <form className="flex flex-wrap items-center gap-2"
        onSubmit={(e) => { e.preventDefault(); setQuery(lookup.trim()); }}>
        <div className="relative min-w-[280px] flex-1">
          <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input value={lookup} onChange={(e) => setLookup(e.target.value)}
            placeholder="Find a member by name, email or user ID"
            className="h-9 pl-8 text-[13px]" data-testid="oa-access-lookup-input" />
        </div>
        <Button type="submit" size="sm" className="h-9" data-testid="oa-access-lookup-submit">
          Search
        </Button>
      </form>

      {query.length >= 2 && (
        <Card className="p-2" data-testid="oa-access-search-results">
          {(search.data?.users || []).length === 0 ? (
            <p className="px-2 py-1.5 text-[12px] text-muted-foreground"
              data-testid="oa-access-search-empty">
              {search.isLoading ? "Searching…" : "No member matches that search."}
            </p>
          ) : (
            <ul className="divide-y divide-border">
              {search.data.users.map((u) => (
                <li key={u.user_id}>
                  <button type="button"
                    onClick={() => { setUserId(u.user_id); setQuery(""); setLookup(""); }}
                    className={`flex w-full flex-wrap items-center gap-2 px-2 py-2 text-left text-[12px] transition-colors hover:bg-secondary ${
                      userId === u.user_id ? "bg-teal-50" : ""}`}
                    data-testid={`oa-access-search-result-${u.user_id}`}>
                    <span className="font-[640]">
                      {[u.first_name, u.last_name].filter(Boolean).join(" ") || "(no name)"}
                    </span>
                    <span className="text-muted-foreground">{u.email}</span>
                    <span className="ml-auto font-mono text-[11px] text-muted-foreground">
                      {u.user_id}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Card>
      )}

      {!userId ? (
        <p className="text-[12px] text-muted-foreground" data-testid="oa-access-hint">
          Search for a member by name, email or user ID to review their Otter Assist access and
          overrides.
        </p>
      ) : (
        <>
          <Card className="p-4" data-testid="oa-access-panel">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h3 className="text-sm font-[640]">Otter Assist access</h3>
                <p className="mt-0.5 text-[12px] font-[600]" data-testid="oa-access-member">
                  {member ? (member.name || "(no name)") : "Resolving…"}
                </p>
                <p className="text-[11px] text-muted-foreground"
                  data-testid="oa-access-member-email">{member?.email}</p>
                <p className="font-mono text-[11px] text-muted-foreground"
                  data-testid="oa-access-member-id">{member?.user_id || userId}</p>
                <div className="mt-2 flex items-center gap-2">
                  <Badge variant={disabled ? "destructive" : "default"}
                    data-testid="oa-access-state">
                    {disabled ? "disabled" : "active"}
                  </Badge>
                  {disabled && (
                    <span className="text-[11px] text-muted-foreground">
                      since {access.data?.state?.since?.slice(0, 10)}
                    </span>
                  )}
                </div>
                <p className="mt-2 max-w-xl text-[11px] text-muted-foreground">
                  This is not an account suspension. {DISABLE_EFFECT}
                </p>
              </div>
              <Button size="sm" variant={disabled ? "outline" : "destructive"}
                onClick={() => setDialog(disabled ? "restore" : "disable")}
                data-testid={disabled ? "oa-access-restore" : "oa-access-disable"}>
                <UserX className="mr-1.5 h-3.5 w-3.5" />
                {disabled ? "Restore access" : "Disable Otter Assist access"}
              </Button>
            </div>
            {!!access.data?.history?.length && (
              <ul className="mt-3 space-y-1 border-t border-border pt-3 text-[11px]"
                data-testid="oa-access-history">
                {access.data.history.map((row) => (
                  <li key={row.id} className="flex flex-wrap gap-2 text-muted-foreground">
                    <span className="font-[600] text-foreground">{row.state}</span>
                    <span>{row.created_at?.slice(0, 16).replace("T", " ")}</span>
                    <span>— {row.reason}</span>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          <Card className="p-4" data-testid="oa-overrides-panel">
            <h3 className="text-sm font-[640]">Per-user overrides</h3>
            <p className="mt-0.5 text-[12px] text-muted-foreground">
              Append-only. Nothing is ever deleted: revoking stamps a record and an expiry simply
              stops applying. Leave the expiry empty for a permanent override.
            </p>

            <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1.5 text-[12px]">
              {Object.entries(overrides.data?.effective || {}).map(([k, info]) => (
                <React.Fragment key={k}>
                  <dt className="text-muted-foreground">{KIND_LABEL[k] || k}</dt>
                  <dd className="text-right font-[600]" data-testid={`oa-override-effective-${k}`}>
                    {info.override === null || info.override === undefined
                      ? `default (${info.base_source})` : info.override}
                  </dd>
                </React.Fragment>
              ))}
            </dl>

            <div className="mt-4 flex flex-wrap items-end gap-2 border-t border-border pt-3">
              <Select value={kind} onValueChange={setKind}>
                <SelectTrigger className="h-9 w-[240px] text-[13px]" data-testid="oa-override-kind">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(overrides.data?.kinds || Object.keys(KIND_LABEL)).map((k) => (
                    <SelectItem key={k} value={k}>{KIND_LABEL[k] || k}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input type="number" value={value} onChange={(e) => setValue(e.target.value)}
                placeholder="Value" className="h-9 w-[110px] text-[13px]"
                data-testid="oa-override-value" />
              <label className="text-[11px] font-[600] uppercase tracking-wide text-muted-foreground">
                Expires (blank = permanent)
                <Input type="date" value={expires} onChange={(e) => setExpires(e.target.value)}
                  className="mt-1 h-9 w-[160px] text-[13px]" data-testid="oa-override-expires" />
              </label>
              <Button size="sm" className="h-9" disabled={value === ""}
                onClick={() => setDialog("override")} data-testid="oa-override-apply">
                Apply override
              </Button>
            </div>

            {!!overrides.data?.history?.length && (
              <ul className="mt-3 divide-y divide-border border-t border-border text-[11px]"
                data-testid="oa-override-history">
                {overrides.data.history.map((row) => (
                  <li key={row.id} className="flex flex-wrap items-center gap-2 py-1.5">
                    <Badge variant={row.active ? "default" : "secondary"}>
                      {row.active ? "active" : (row.revoked_at ? "revoked" : "expired")}
                    </Badge>
                    <span className="font-[600]">{KIND_LABEL[row.kind] || row.kind}</span>
                    <span className="tabular-nums">= {row.value}</span>
                    <span className="text-muted-foreground">
                      {row.permanent ? "permanent" : `until ${row.expires_at?.slice(0, 10)}`}
                    </span>
                    <span className="text-muted-foreground">— {row.reason}</span>
                    {row.active && (
                      <Button size="sm" variant="ghost" className="ml-auto h-6 text-[11px]"
                        onClick={() => setDialog({ revoke: row.id })}
                        data-testid={`oa-override-revoke-${row.id}`}>Revoke</Button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </>
      )}

      <OaReasonDialog
        open={dialog === "disable"} onOpenChange={(o) => !o && setDialog(null)} testId="disable"
        title="Disable this member's Otter Assist access?"
        description="Their Startup Otter account is unaffected."
        target={memberLine} effect={DISABLE_EFFECT} reversible allowSilent
        destructive confirmLabel="Disable access"
        invalidate={[["oa-admin-access", userId]]}
        mutationFn={(body) => api.post(`/admin/otter-assist/access/${userId}/disable`, body)}
        onDone={() => setDialog(null)} />

      <OaReasonDialog
        open={dialog === "restore"} onOpenChange={(o) => !o && setDialog(null)} testId="restore"
        title="Restore this member's Otter Assist access?"
        description="Future eligibility only." target={memberLine}
        effect="Nothing missed while access was paused is resent, replayed or reopened."
        reversible allowSilent confirmLabel="Restore access"
        invalidate={[["oa-admin-access", userId]]}
        mutationFn={(body) => api.post(`/admin/otter-assist/access/${userId}/restore`, body)}
        onDone={() => setDialog(null)} />

      <OaReasonDialog
        open={dialog === "override"} onOpenChange={(o) => !o && setDialog(null)} testId="override"
        title="Apply this override?" description="The member's own preference is never changed."
        target={`${memberLine} — ${KIND_LABEL[kind]} = ${value}`}
        effect={expires ? `Applies until ${expires}, then the default applies again.`
          : "Permanent until an admin revokes it."}
        reversible confirmLabel="Apply override"
        invalidate={[["oa-admin-overrides", userId]]}
        mutationFn={({ reason, internal_note }) => api.post(
          `/admin/otter-assist/overrides/${userId}`,
          { kind, value: Number(value), reason, internal_note,
            expires_at: expires ? `${expires}T00:00:00+00:00` : null })}
        onDone={() => { setDialog(null); setValue(""); setExpires(""); }} />

      <OaReasonDialog
        open={!!dialog?.revoke} onOpenChange={(o) => !o && setDialog(null)} testId="revoke"
        title="Revoke this override?" description="The record is kept as history, never deleted."
        target={dialog?.revoke} effect="The canonical default applies again immediately."
        reversible allowInternalNote={false} confirmLabel="Revoke"
        invalidate={[["oa-admin-overrides", userId]]}
        mutationFn={({ reason }) => api.post(
          `/admin/otter-assist/overrides/${dialog.revoke}/revoke`, { reason })}
        onDone={() => setDialog(null)} />
    </div>
  );
}

export default OaAccess;
